import { memo, useMemo, useState } from "react";
import { CardSelect } from "ui/components/CardSelect";
import { IMPORT_COST_OPTIONS, MARKETPLACE_CHANNEL_CODE, initialChannelDataEntry } from "../../consts";
import { MarketplacePayment } from "../../types";
import { PriceStepProps } from "./types";
import ChannelPriceRow from "./ChannelPriceRow";
import PriceAdjustModal from "./PriceAdjustModal";

// Korak unosa cijene. Svaki odabrani kanal ima vlastitu, neovisnu cijenu —
// klik na "Prilagodi" otvara modal s NumberField-om i checkbox-om "Primijeni
// na sve oglasnike". Marketplace kanal je tretiran kao i ostali, samo njegova
// cijena dodatno sinkronizira `formValues.basePrice` (BE legacy field).
const PriceStep: React.FC<PriceStepProps> = (props) => {
  const { formValues, setFormValues, errors, onChannelDataChange } = props;
  const { payment } = formValues.marketplaceData;

  const channelsToShow = useMemo(
    () =>
      [...formValues.channels]
        .filter((c) => formValues.selectedChannelIds.includes(c.id))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [formValues.channels, formValues.selectedChannelIds]
  );

  const marketplaceChannelId = useMemo(
    () => formValues.channels.find((c) => c.code === MARKETPLACE_CHANNEL_CODE)?.id ?? null,
    [formValues.channels]
  );

  // Vraća prikazanu cijenu za kanal: per-kanal `priceOverride` ako postoji,
  // inače fallback na `basePrice` (kreira UX bez "—" praznine za marketplace
  // koji u bootstrapu nije eksplicitno pre-popunjen).
  const getChannelPrice = (channelId: string): number | null => {
    const override = formValues.channelData[channelId]?.priceOverride;
    if (override) return Number(override);
    if (channelId === marketplaceChannelId && formValues.basePrice) {
      return Number(formValues.basePrice);
    }
    return null;
  };

  // === Modal state ===
  const [adjust, setAdjust] = useState<{ channelId: string; channelName: string; price: number | null } | null>(null);

  const handleOpenAdjust = (channelId: string, price: number | null, channelName: string) => {
    setAdjust({ channelId, channelName, price });
  };

  const handleCloseAdjust = () => setAdjust(null);

  const handleSaveAdjust = (value: number | null, applyToAll: boolean) => {
    if (!adjust) return;
    const valueStr = value != null ? String(value) : "";

    setFormValues((prev) => {
      if (applyToAll) {
        // Upisuje istu vrijednost u sve odabrane kanale + basePrice
        // (marketplace channelData se mapira u priceOverride radi konzistencije).
        const nextChannelData = { ...prev.channelData };
        for (const chId of prev.selectedChannelIds) {
          const existing = nextChannelData[chId] ?? initialChannelDataEntry;
          nextChannelData[chId] = { ...existing, priceOverride: valueStr };
        }
        return {
          ...prev,
          basePrice: valueStr,
          channelData: nextChannelData,
        };
      }

      // Samo jedan kanal. Marketplace dodatno sinkronizira basePrice.
      const existing = prev.channelData[adjust.channelId] ?? initialChannelDataEntry;
      const updatedEntry = { ...existing, priceOverride: valueStr };
      const isMarketplace = adjust.channelId === marketplaceChannelId;
      return {
        ...prev,
        basePrice: isMarketplace ? valueStr : prev.basePrice,
        channelData: { ...prev.channelData, [adjust.channelId]: updatedEntry },
      };
    });

    // Sinkroniziraj postojeći `onChannelDataChange` callback (ako parent prati
    // izmjene). Za apply-to-all ovdje šaljemo samo trenutni — alternativa bi
    // bila više poziva, ali to nije potrebno jer setFormValues iznad već
    // ažurira cijeli state.
    if (!applyToAll) {
      const existing = formValues.channelData[adjust.channelId] ?? initialChannelDataEntry;
      onChannelDataChange(adjust.channelId, { ...existing, priceOverride: valueStr });
    }

    setAdjust(null);
  };

  // CardSelect vraća id opcije kao string, a `priceIncludesImport` prima samo "with"/"without".
  const handleImportChange = (next: string) => {
    setFormValues((prev) => ({
      ...prev,
      marketplaceData: {
        ...prev.marketplaceData,
        payment: {
          ...prev.marketplaceData.payment,
          priceIncludesImport: next as MarketplacePayment["priceIncludesImport"],
        },
      },
    }));
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-6 py-8">
      <div className="flex flex-col items-center text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-800">Cijena vozila</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md">
          Postavite cijenu za svaki oglasnik zasebno. Pri prilagodbi možete označiti "Primijeni na sve" za istovremenu izmjenu.
        </p>
      </div>

      {channelsToShow.length > 0 && (
        <div>
          <div className="text-sm font-medium text-slate-700 mb-3">Cijena po oglasniku</div>
          {/* Bez poruke iznad liste — sam redak bez cijene se oboji, pa se vidi na kojem
              oglasniku cijena nedostaje. Razlog i dalje javlja snackbar iz validacije. */}
          <div className="flex flex-col gap-2">
            {channelsToShow.map((ch) => {
              const price = getChannelPrice(ch.id);

              return (
                <ChannelPriceRow
                  key={ch.id}
                  channel={ch}
                  price={price}
                  onAdjust={handleOpenAdjust}
                  hasError={!!errors.basePrice && price == null}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 mb-8">
        <div className="text-sm font-medium text-slate-700 mb-3">Cijena uključuje troškove uvoza</div>
        <CardSelect
          options={IMPORT_COST_OPTIONS}
          value={payment.priceIncludesImport}
          onChange={handleImportChange}
        />
      </div>

      <PriceAdjustModal
        show={adjust !== null}
        channelName={adjust?.channelName ?? null}
        initialValue={adjust?.price ?? null}
        onSave={handleSaveAdjust}
        onClose={handleCloseAdjust}
      />
    </div>
  );
};

export default memo(PriceStep);
